import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths, startOfWeek, endOfWeek, isSameMonth, parseISO } from 'date-fns';
import SupervisorLayout from '../../components/layout/SupervisorLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Calendar as CalendarIcon, 
  Plus,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Users,
  Search,
  Briefcase
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SupervisorShiftsPage() {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  // Form state
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [datePickerMonth, setDatePickerMonth] = useState(new Date());
  const [staffSearch, setStaffSearch] = useState('');
  const [shiftForm, setShiftForm] = useState({
    start_time: '09:00',
    end_time: '17:00',
    site_name: '',
    postcode: '',
    shift_role: ''
  });

  const fetchData = useCallback(async () => {
    const start = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
    const end = format(endOfMonth(currentMonth), 'yyyy-MM-dd');
    
    try {
      const [shiftsRes, empRes] = await Promise.all([
        axios.get(`${API}/shifts/calendar`, { params: { start_date: start, end_date: end } }),
        axios.get(`${API}/supervisor/employees`)
      ]);
      setShifts(shiftsRes.data);
      setEmployees(empRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load shifts');
    } finally {
      setLoading(false);
    }
  }, [currentMonth]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter employees by search term
  const filteredEmployees = employees.filter(emp =>
    emp.name.toLowerCase().includes(staffSearch.toLowerCase())
  );

  const toggleEmployee = (empId) => {
    setSelectedEmployees(prev => 
      prev.includes(empId) 
        ? prev.filter(id => id !== empId)
        : [...prev, empId]
    );
  };

  const selectAllEmployees = () => {
    if (selectedEmployees.length === employees.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(employees.map(e => e.id));
    }
  };

  const resetForm = () => {
    setSelectedEmployees([]);
    setSelectedDates([]);
    setDatePickerMonth(new Date());
    setStaffSearch('');
    setShiftForm({
      start_time: '09:00',
      end_time: '17:00',
      site_name: '',
      postcode: '',
      shift_role: ''
    });
  };

  // Toggle date selection
  const toggleDateSelection = (dateStr) => {
    setSelectedDates(prev => 
      prev.includes(dateStr)
        ? prev.filter(d => d !== dateStr)
        : [...prev, dateStr].sort()
    );
  };

  // Get calendar days for the date picker
  const getDatePickerDays = () => {
    const monthStart = startOfMonth(datePickerMonth);
    const monthEnd = endOfMonth(datePickerMonth);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  };

  const handleCreateShifts = async (e) => {
    e.preventDefault();
    if (selectedEmployees.length === 0) {
      toast.error('Please select at least one employee');
      return;
    }

    if (selectedDates.length === 0) {
      toast.error('Please select at least one date');
      return;
    }

    setCreating(true);

    // Use the standalone batch endpoint — sends ONE summary email per
    // employee covering every selected date, rather than one email per
    // (employee × date) combination.
    try {
      const res = await axios.post(`${API}/shifts/batch-standalone`, {
        employee_ids: selectedEmployees,
        dates: selectedDates,
        start_time: shiftForm.start_time,
        end_time: shiftForm.end_time,
        site_name: shiftForm.site_name,
        postcode: shiftForm.postcode,
        shift_role: shiftForm.shift_role,
      });
      const c = res.data?.created || 0;
      const s = res.data?.skipped || 0;
      const e = res.data?.emails_sent || 0;
      setCreating(false);
      if (c > 0) {
        toast.success(`Created ${c} shift(s)${e ? ` · ${e} email(s) sent` : ''}`);
      }
      if (s > 0) {
        toast.warning(`Skipped ${s} (overlap or staff missing)`);
      }
      if (c === 0 && s === 0) {
        toast.error('No shifts were created');
      }
    } catch (error) {
      setCreating(false);
      toast.error(error.response?.data?.detail || 'Failed to create shifts');
      return;
    }

    setCreateOpen(false);
    resetForm();
    fetchData();
  };

  // Calendar helpers
  const days = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth)
  });

  const getShiftsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return shifts.filter(s => s.date === dateStr);
  };

  const selectedDateShifts = getShiftsForDate(selectedDate);

  return (
    <SupervisorLayout>
      <div className="space-y-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-6">
          <div>
            <h1 className="text-2xl font-barlow tracking-wider text-primary uppercase flex items-center gap-2">
              <CalendarIcon className="w-6 h-6" />
              Shift Calendar
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Create and view shifts for employees
            </p>
          </div>
          <Button
            onClick={() => setCreateOpen(true)}
            className="bg-primary hover:bg-primary/90 text-black rounded-sm"
            data-testid="add-shift-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Shift
          </Button>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Calendar */}
          <Card className="lg:col-span-2 bg-card border-border rounded-sm">
            <CardContent className="p-4">
              {/* Month navigation */}
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-barlow tracking-wider uppercase">
                  {format(currentMonth, 'MMMM yyyy')}
                </h2>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Days of week */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                  <div key={day} className="text-center text-xs text-muted-foreground py-2 font-medium">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar days - White bg, Black text, Red for shifts, Yellow for selected */}
              <div className="grid grid-cols-7 gap-1">
                {/* Empty cells for days before month starts */}
                {Array.from({ length: days[0].getDay() }).map((_, i) => (
                  <div key={`empty-${i}`} className="aspect-square" />
                ))}
                
                {days.map(day => {
                  const dayShifts = getShiftsForDate(day);
                  const hasShifts = dayShifts.length > 0;
                  const isSelected = format(day, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');
                  
                  return (
                    <button
                      key={day.toISOString()}
                      onClick={() => setSelectedDate(day)}
                      className={cn(
                        "aspect-square flex flex-col items-center justify-center rounded-sm transition-all text-sm font-medium",
                        // Base: white background, black text
                        "bg-white text-black",
                        // Yellow when selected (priority over red)
                        isSelected && "!bg-yellow-400 !text-black ring-2 ring-yellow-500",
                        // Red when has shifts (but not if selected)
                        hasShifts && !isSelected && "!bg-red-500 !text-foreground",
                        // Hover effect
                        !isSelected && !hasShifts && "hover:bg-accent"
                      )}
                    >
                      <span>{format(day, 'd')}</span>
                      {hasShifts && (
                        <span className="text-[9px] font-bold">
                          {dayShifts.length}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Selected date shifts */}
          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-4">
              <h3 className="font-barlow tracking-wider uppercase text-sm mb-4 text-primary">
                {format(selectedDate, 'EEEE, MMM d')}
              </h3>
              
              {selectedDateShifts.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No shifts scheduled
                </p>
              ) : (
                <div className="space-y-3">
                  {selectedDateShifts.map(shift => (
                    <div
                      key={shift.id}
                      className="p-3 bg-background rounded-sm border border-border"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium text-sm">{shift.employee_name}</span>
                          {shift.is_extra && (
                            <span className="text-[9px] uppercase tracking-wider bg-amber-500/15 text-amber-300 border border-amber-500/40 rounded-sm px-1.5 py-0.5" title="Added as extra (over planned capacity)">
                              + Extra
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {shift.start_time && shift.end_time ? (
                            <>{shift.start_time} - {shift.end_time}</>
                          ) : shift.start_time || shift.end_time ? (
                            <span>
                              {shift.start_time ? (
                                <span className="text-foreground">Start {shift.start_time}</span>
                              ) : (
                                <span className="text-amber-400 font-semibold uppercase tracking-wider">Start Pending</span>
                              )}
                              <span className="mx-1">•</span>
                              {shift.end_time ? (
                                <span className="text-foreground">End {shift.end_time}</span>
                              ) : (
                                <span className="text-amber-400 font-semibold uppercase tracking-wider">End Pending</span>
                              )}
                            </span>
                          ) : (
                            <span className="text-amber-400 font-semibold uppercase tracking-wider px-1.5 py-0.5 bg-amber-500/10 border border-amber-500/30 rounded-sm text-[10px]">
                              Pending
                            </span>
                          )}
                        </span>
                      </div>
                      {shift.site_name && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <MapPin className="w-3 h-3" />
                          {shift.site_name}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Create Shift Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="bg-card border-border rounded-sm max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-barlow tracking-wider uppercase">
                Create Shifts
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateShifts} className="space-y-5 mt-4">
              
              {/* Employee Selection */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Select Employees ({selectedEmployees.length})
                  </Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={selectAllEmployees}
                    className="text-xs text-primary h-6"
                  >
                    {selectedEmployees.length === employees.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>
                {/* Search Bar */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search employees..."
                    value={staffSearch}
                    onChange={(e) => setStaffSearch(e.target.value)}
                    className="pl-10 bg-background border-border rounded-sm h-9 text-sm"
                    data-testid="staff-search-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 bg-background rounded-sm border border-border scrollbar-thin">
                  {filteredEmployees.length > 0 ? filteredEmployees.map((emp) => (
                    <button
                      type="button"
                      key={emp.id}
                      className={cn(
                        "flex items-center gap-2 p-2 rounded-sm cursor-pointer transition-colors text-sm text-left",
                        selectedEmployees.includes(emp.id) 
                          ? "bg-primary/20 border border-primary text-primary" 
                          : "hover:bg-accent/50 border border-transparent"
                      )}
                      onClick={() => toggleEmployee(emp.id)}
                    >
                      <span className={cn(
                        "w-4 h-4 rounded-sm border flex items-center justify-center text-xs",
                        selectedEmployees.includes(emp.id) 
                          ? "bg-primary border-primary text-black" 
                          : "border-border"
                      )}>
                        {selectedEmployees.includes(emp.id) && "✓"}
                      </span>
                      <span className="truncate">{emp.name}</span>
                    </button>
                  )) : (
                    <p className="text-sm text-muted-foreground col-span-2 text-center py-4">
                      {employees.length === 0 ? 'No employees available.' : 'No matching employees found.'}
                    </p>
                  )}
                </div>
              </div>

              {/* Date Selection - Multi-day clickable calendar */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Select Dates ({selectedDates.length} selected)
                  </Label>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setDatePickerMonth(subMonths(datePickerMonth, 1))}
                      className="h-6 w-6"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-xs font-medium min-w-[100px] text-center">
                      {format(datePickerMonth, 'MMM yyyy')}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setDatePickerMonth(addMonths(datePickerMonth, 1))}
                      className="h-6 w-6"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="bg-background border border-border rounded-sm p-2">
                  {/* Day headers */}
                  <div className="grid grid-cols-7 gap-1 mb-1">
                    {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                      <div key={day} className="text-center text-[10px] text-muted-foreground py-1">
                        {day}
                      </div>
                    ))}
                  </div>
                  {/* Calendar days */}
                  <div className="grid grid-cols-7 gap-1">
                    {getDatePickerDays().map(day => {
                      const dateStr = format(day, 'yyyy-MM-dd');
                      const isSelected = selectedDates.includes(dateStr);
                      const isCurrentMonth = isSameMonth(day, datePickerMonth);
                      const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                      
                      return (
                        <button
                          key={dateStr}
                          type="button"
                          onClick={() => toggleDateSelection(dateStr)}
                          className={cn(
                            "aspect-square flex items-center justify-center text-xs rounded-sm transition-all",
                            isSelected 
                              ? "bg-primary text-black font-bold" 
                              : "hover:bg-accent",
                            !isCurrentMonth && "opacity-30",
                            isToday && !isSelected && "border border-primary/50"
                          )}
                        >
                          {format(day, 'd')}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {selectedDates.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Selected: {selectedDates.map(d => format(parseISO(d), 'MMM d')).join(', ')}
                  </p>
                )}
              </div>

              {/* Time Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Start Time
                  </Label>
                  <Input
                    type="time"
                    value={shiftForm.start_time}
                    onChange={(e) => setShiftForm({...shiftForm, start_time: e.target.value})}
                    className="bg-background border-border rounded-sm"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    End Time
                  </Label>
                  <Input
                    type="time"
                    value={shiftForm.end_time}
                    onChange={(e) => setShiftForm({...shiftForm, end_time: e.target.value})}
                    className="bg-background border-border rounded-sm"
                    required
                  />
                </div>
              </div>

              {/* Site Details */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Site Name
                  </Label>
                  <Input
                    placeholder="e.g., Main Office"
                    value={shiftForm.site_name}
                    onChange={(e) => setShiftForm({...shiftForm, site_name: e.target.value})}
                    className="bg-background border-border rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Postcode
                  </Label>
                  <Input
                    placeholder="e.g., SW1A 1AA"
                    value={shiftForm.postcode}
                    onChange={(e) => setShiftForm({...shiftForm, postcode: e.target.value})}
                    className="bg-background border-border rounded-sm"
                  />
                </div>
              </div>

              {/* Shift Role */}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                  <Briefcase className="w-3 h-3" /> Role
                </Label>
                <Select value={shiftForm.shift_role} onValueChange={(val) => setShiftForm({...shiftForm, shift_role: val})}>
                  <SelectTrigger className="bg-background border-border rounded-sm" data-testid="sup-create-shift-role-select">
                    <SelectValue placeholder="Select role..." />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="Cleaner">Cleaner</SelectItem>
                    <SelectItem value="Dual role (SIA, cleaner)">Dual role (SIA, cleaner)</SelectItem>
                    <SelectItem value="SIA Door Supervisor">SIA Door Supervisor</SelectItem>
                    <SelectItem value="Steward (CSO)">Steward (CSO)</SelectItem>
                    <SelectItem value="Event Supervisor">Event Supervisor</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setCreateOpen(false);
                    resetForm();
                  }}
                  className="border-border"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={creating || selectedEmployees.length === 0 || selectedDates.length === 0}
                  className="bg-primary hover:bg-primary/90 text-black"
                >
                  {creating ? 'Creating...' : `Create ${selectedEmployees.length * selectedDates.length} Shift(s)`}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </SupervisorLayout>
  );
}
